import React, { useRef } from "react";
import { X } from "lucide-react";
import useScrollLock from "../../../hooks/useScrollLock";
import "./LocationPermissionModal.css";

interface LocationPermissionModalProps {
  isOpen: boolean;
  stage: "pre-prompt" | "denied";
  onAllow: () => void;
  onClose: () => void;
  /** Overrides the default "Allow Location" pre-prompt copy for this context. */
  preTitle?: string;
  preSubtitle?: string;
  /** Overrides the default "Location Blocked" denied-stage copy for this context. */
  deniedSubtitle?: string;
}

const LocationPermissionModal: React.FC<LocationPermissionModalProps> = ({
  isOpen,
  stage,
  onAllow,
  onClose,
  preTitle,
  preSubtitle,
  deniedSubtitle,
}) => {
  const overlayRef = useRef<HTMLDivElement>(null);

  // Lock ALL background scrolling when modal is open using the reusable hook
  useScrollLock(isOpen, overlayRef, ".lpm-modal");

  if (!isOpen) return null;

  return (
    <div className="lpm-overlay" ref={overlayRef} onClick={onClose}>
      <div className="lpm-modal" onClick={(e) => e.stopPropagation()}>
        <button className="lpm-close-btn" onClick={onClose} aria-label="Close">
          <X size={18} />
        </button>

        {stage === "pre-prompt" && (
          <>
            <div className="lpm-icon">📍</div>
            <h2 className="lpm-title">{preTitle ?? "Allow Location"}</h2>
            <p className="lpm-subtitle">
              {preSubtitle ?? "We use your location to show your listing to nearby buyers."}
            </p>
            <div className="lpm-hint">
              Tap <strong>"Allow"</strong> when your browser asks.
            </div>
            <div className="lpm-actions">
              <button className="lpm-btn-secondary" onClick={onClose}>Cancel</button>
              <button className="lpm-btn-primary" onClick={onAllow}>
                Continue
              </button>
            </div>
          </>
        )}

        {stage === "denied" && (
          <>
            <div className="lpm-icon">🔒</div>
            <h2 className="lpm-title">Location Blocked</h2>
            <p className="lpm-subtitle">
              {deniedSubtitle ?? "To enforce fair use among listed products, location is mandatory to create a product in NineFinds. Please enable it to continue."}
            </p>
            <div className="lpm-steps">
              <div className="lpm-step">
                <span className="lpm-step-num">1</span>
                <span>Make sure <strong>Location / GPS</strong> is turned on in your phone's settings</span>
              </div>
              <div className="lpm-step">
                <span className="lpm-step-num">2</span>
                <span>Tap the icon left to the URL</span>
              </div>
              <div className="lpm-step">
                <span className="lpm-step-num">3</span>
                <span>Tap <strong>Permissions</strong> or <strong>Site settings</strong></span>
              </div>
              <div className="lpm-step">
                <span className="lpm-step-num">4</span>
                <span>Set <strong>Location</strong> to <strong>Allow</strong></span>
              </div>
            </div>
            <div className="lpm-actions">
              <button className="lpm-btn-secondary" onClick={onClose}>Cancel</button>
              <button className="lpm-btn-primary" onClick={onAllow}>
                Try Again
              </button>
            </div>
            <p className="lpm-footer-note">
              If it still doesn't work, refresh the page after enabling.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default LocationPermissionModal;
